// frontend/src/pages/CompetitorResearch.js
// Compare every active in-stock SKU's price against eBay competitor median.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../App';

const FILTERS = [
  { id: 'all',         label: 'All' },
  { id: 'overpriced',  label: 'Overpriced (>10%)' },
  { id: 'underpriced', label: 'Underpriced (>10%)' },
  { id: 'has_data',    label: 'Has data' },
  { id: 'no_data',     label: 'No data' },
  { id: 'stale',       label: 'Stale / never researched' },
];

const SORTS = [
  { id: 'delta_desc',  label: 'Most overpriced' },
  { id: 'delta_asc',   label: 'Most underpriced' },
  { id: 'price_desc',  label: 'My price ↓' },
  { id: 'price_asc',   label: 'My price ↑' },
  { id: 'median_desc', label: 'Comp median ↓' },
  { id: 'researched',  label: 'Last researched' },
  { id: 'sku',         label: 'SKU A–Z' },
];

function fmtPrice(v) {
  return v != null && !isNaN(v) ? '$' + parseFloat(v).toFixed(2) : '—';
}

function fmtAge(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3.6e6);
  if (h < 1)   return 'just now';
  if (h < 24)  return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 7)   return d + 'd ago';
  return Math.floor(d / 7) + 'w ago';
}

// Infer pack/multi count from a listing title so the UI can show per-unit price.
// Fuzzy by nature (titles are free text) — used only for DISPLAY, never to alter
// the median. Returns 1 when nothing pack-like is found.
function parsePack(title) {
  if (!title) return 1;
  const t = String(title).toLowerCase();
  let m = t.match(/\b(?:set|pack|lot|case|bundle|box)\s*of\s*(\d{1,3})\b/);
  if (m) return parseInt(m[1]);
  m = t.match(/\b(\d{1,3})\s*[- ]?\s*(?:pack|pk|pieces?|pcs?|count|ct|bottles?|cans?|units?|pairs?)\b/);
  if (m) return parseInt(m[1]);
  m = t.match(/\bqty\s*(\d{1,3})\b/);
  if (m) return parseInt(m[1]);
  return 1;
}

function deltaColor(d) {
  if (d == null) return 'var(--muted)';
  if (d >= 10)   return 'var(--danger)';
  if (d >= 3)    return 'var(--warn)';
  if (d <= -10)  return 'var(--accent2)';
  return 'var(--text)';
}

function PriceEditor({ editor, onChange, onClose, onApply, busy }) {
  const { sku, title, current, median, min, max, listings, researched_at, customPrice } = editor;
  const setCustom = v => onChange({ ...editor, customPrice: v });
  const newPrice = parseFloat(customPrice);
  const newDelta = median > 0 && !isNaN(newPrice) ? Math.round(((newPrice - median) / median) * 1000) / 10 : null;
  const dColor = newDelta == null ? 'var(--muted)' : newDelta >= 10 ? 'var(--danger)' : newDelta >= 3 ? 'var(--warn)' : newDelta <= -10 ? 'var(--accent2)' : 'var(--text)';
  const quick = [
    median != null && { label: 'Match median',  v: median },
    min != null    && { label: 'Match min',     v: min },
    max != null    && { label: 'Match max',     v: max },
    median != null && { label: '−5% vs median', v: Math.round(median * 0.95 * 100) / 100 },
    median != null && { label: '−10% vs median',v: Math.round(median * 0.90 * 100) / 100 },
  ].filter(Boolean);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:2000, display:'flex',
      alignItems:'center', justifyContent:'center' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width:'100%', maxWidth:560, maxHeight:'85vh', overflowY:'auto', padding:0 }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ fontWeight:700, fontSize:15 }}>Set price</div>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--accent2)', marginTop:2 }}>{sku}</div>
              <div style={{ fontSize:12, color:'var(--muted)', marginTop:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{title}</div>
            </div>
            <button className="btn btn-ghost" onClick={onClose} style={{ padding:'4px 10px' }}>✕</button>
          </div>
        </div>

        <div style={{ padding:'16px 20px' }}>
          {/* Reference prices */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8, marginBottom:12 }}>
            {[
              { label:'My current', v: current,  c:'var(--text)' },
              { label:'Comp min',   v: min,      c:'var(--accent2)' },
              { label:'Comp median',v: median,   c:'var(--accent)' },
              { label:'Comp max',   v: max,      c:'var(--warn)' },
            ].map(s => (
              <div key={s.label} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 8px', textAlign:'center' }}>
                <div style={{ fontSize:18, fontWeight:700, color:s.c }}>{s.v != null ? '$' + parseFloat(s.v).toFixed(2) : '—'}</div>
                <div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Quick-pick */}
          {quick.length > 0 && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
              {quick.map(q => (
                <button key={q.label} className="btn btn-ghost" onClick={() => setCustom(q.v.toFixed(2))}
                  style={{ fontSize:11, padding:'5px 10px' }}>
                  {q.label} <strong style={{ marginLeft:4 }}>${q.v.toFixed(2)}</strong>
                </button>
              ))}
            </div>
          )}

          {/* Custom input */}
          <div className="form-group" style={{ marginBottom:12 }}>
            <label>New price ($)</label>
            <input type="number" min="0" step="0.01" value={customPrice}
              onChange={e => setCustom(e.target.value)}
              style={{ fontSize:18, fontWeight:600 }} />
            {newDelta != null && (
              <div style={{ fontSize:11, color:dColor, marginTop:4 }}>
                {newDelta > 0 ? '+' : ''}{newDelta.toFixed(1)}% vs comp median
              </div>
            )}
          </div>

          {/* Sources */}
          {listings && listings.length > 0 && (
            <details style={{ marginBottom:12 }}>
              <summary style={{ cursor:'pointer', fontSize:12, color:'var(--muted)' }}>
                Sources ({listings.length} competitor listings) — researched {fmtAge(researched_at)}
              </summary>
              <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:4 }}>
                {listings.map(l => (
                  <a key={l.itemId} href={l.url} target="_blank" rel="noreferrer"
                    style={{ display:'flex', justifyContent:'space-between', gap:8, padding:'6px 10px',
                      background:'var(--surface)', border:'1px solid var(--border)', borderRadius:6,
                      textDecoration:'none', color:'var(--text)', fontSize:12 }}>
                    <span style={{ minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
                      {l.title || l.itemId}
                    </span>
                    <span style={{ fontWeight:700, color:'var(--accent2)', flexShrink:0 }}>
                      {l.price != null ? '$' + l.price.toFixed(2) : '—'}
                    </span>
                  </a>
                ))}
              </div>
            </details>
          )}
          {(!listings || listings.length === 0) && (
            <div style={{ fontSize:12, color:'var(--muted)', marginBottom:12 }}>
              No source listings stored yet — refresh this SKU to populate sources.
            </div>
          )}
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, padding:'12px 20px', borderTop:'1px solid var(--border)' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || isNaN(newPrice) || newPrice <= 0}
            onClick={() => onApply(sku, newPrice, median)}>
            {busy ? '⏳ Saving...' : 'Save price → push to channels'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SourcesModal({ data, onClose }) {
  const { sku, title, listings, researched_at } = data;
  const comp = (listings || []).filter(l => !l.mine);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:2000, display:'flex',
      alignItems:'center', justifyContent:'center' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width:'100%', maxWidth:660, maxHeight:'85vh', overflowY:'auto', padding:0 }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontWeight:700, fontSize:15 }}>Competitor sources</div>
            <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--accent2)', marginTop:2 }}>{sku}</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginTop:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{title}</div>
            <div style={{ fontSize:11, color:'var(--muted)', marginTop:4 }}>{comp.length} competitor listing{comp.length===1?'':'s'} priced · researched {fmtAge(researched_at)}</div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding:'4px 10px' }}>✕</button>
        </div>
        <div style={{ padding:'12px 20px', display:'flex', flexDirection:'column', gap:6 }}>
          {(listings || []).map(l => {
            const pack = parsePack(l.title);
            const delivered = l.delivered != null ? l.delivered : (l.price != null ? l.price + (l.shipping || 0) : null);
            const perUnit = delivered != null && pack > 0 ? delivered / pack : null;
            return (
            <a key={l.itemId} href={l.url} target="_blank" rel="noreferrer"
              style={{ display:'flex', gap:10, alignItems:'center', padding:'8px 12px',
                background: l.mine ? 'rgba(255,214,10,.08)' : 'var(--surface)',
                border:'1px solid var(--border)', borderRadius:6, textDecoration:'none', color:'var(--text)',
                fontSize:12, opacity: l.mine ? 0.65 : 1 }}>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {l.mine && <span style={{ fontSize:10, fontWeight:700, color:'var(--warn)', marginRight:6 }}>YOU</span>}
                  {pack > 1 && <span style={{ fontSize:10, fontWeight:700, color:'var(--accent)', marginRight:6 }}>{pack}-PACK</span>}
                  {l.title || l.itemId}
                </div>
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>
                  {l.condition || 'n/a'} · {l.seller || 'n/a'}
                  {l.shipping != null && <> · ship {l.shipping > 0 ? '$' + Number(l.shipping).toFixed(2) : 'FREE'}</>}
                  {perUnit != null && pack > 1 && <> · <strong style={{ color:'var(--text)' }}>${perUnit.toFixed(2)}/unit</strong></>}
                </div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontWeight:700, color: l.mine ? 'var(--muted)' : 'var(--accent2)',
                  textDecoration: l.mine ? 'line-through' : 'none' }}>
                  {delivered != null ? '$' + Number(delivered).toFixed(2) : '—'}
                </div>
                {l.shipping != null && l.shipping > 0 &&
                  <div style={{ fontSize:10, color:'var(--muted)' }}>${Number(l.price).toFixed(2)} + ship</div>}
              </div>
            </a>
            );
          })}
          {(!listings || listings.length === 0) && (
            <div style={{ fontSize:12, color:'var(--muted)' }}>No source listings stored — refresh this SKU.</div>
          )}
        </div>
        <div style={{ padding:'10px 20px', borderTop:'1px solid var(--border)', fontSize:11, color:'var(--muted)' }}>
          Rows marked <span style={{ color:'var(--warn)', fontWeight:700 }}>YOU</span> are your own listings and are excluded from the median / min / max.
        </div>
      </div>
    </div>
  );
}

export default function CompetitorResearch() {
  const [rows, setRows]       = useState([]);
  const [stats, setStats]     = useState(null);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('overpriced');
  const [sort, setSort]       = useState('delta_desc');
  const [sweepRunning, setSweepRunning] = useState(false);
  const [busy, setBusy]       = useState({});  // sku -> 'refresh' | 'match'
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editor, setEditor] = useState(null);  // { sku, current, median, min, max, listings, customPrice }
  const [sources, setSources] = useState(null);  // { sku, title, listings, researched_at }
  const LIMIT = 50;

  const load = useCallback((p = page) => {
    const params = new URLSearchParams({ page: p, limit: LIMIT, sort, filter });
    if (search) params.set('search', search);
    api.get(`/competitor-research?${params}`).then(r => {
      setRows(r.data.rows);
      setStats(r.data.stats);
      setTotal(r.data.total);
      setSweepRunning(r.data.sweepRunning);
    });
  }, [search, sort, filter, page]);

  useEffect(() => { load(1); setPage(1); }, [filter, sort]); // eslint-disable-line
  useEffect(() => {
    // poll sweep status every 5s if running
    if (!sweepRunning) return;
    const t = setInterval(() => load(page), 5000);
    return () => clearInterval(t);
  }, [sweepRunning, load, page]);

  function handleSearch(e) { e.preventDefault(); setPage(1); load(1); }

  async function refreshOne(sku) {
    setBusy(prev => ({ ...prev, [sku]: 'refresh' }));
    try {
      const r = await api.post(`/competitor-research/refresh/${encodeURIComponent(sku)}`);
      setRows(prev => prev.map(row => row.sku === sku ? { ...row, ...r.data.row } : row));
    } catch (err) {
      alert('Refresh failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusy(prev => { const n = { ...prev }; delete n[sku]; return n; });
    }
  }

  async function applyPrice(sku, newPrice, median) {
    if (newPrice == null || isNaN(newPrice) || newPrice <= 0) return;
    setBusy(prev => ({ ...prev, [sku]: 'match' }));
    try {
      await api.patch(`/products/${encodeURIComponent(sku)}/price`, { price: newPrice });
      const newDelta = median > 0 ? Math.round(((newPrice - median) / median) * 1000) / 10 : null;
      setRows(prev => prev.map(row => row.sku === sku
        ? { ...row, my_price: newPrice, delta_pct: newDelta }
        : row));
      setEditor(null);
    } catch (err) {
      alert('Price update failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusy(prev => { const n = { ...prev }; delete n[sku]; return n; });
    }
  }

  function openEditor(row) {
    setEditor({
      sku: row.sku,
      title: row.title,
      current: parseFloat(row.my_price) || 0,
      median: row.comp_median_price,
      min: row.comp_min_price,
      max: row.comp_max_price,
      listings: row.comp_listings || [],
      researched_at: row.researched_at,
      customPrice: row.comp_median_price != null ? row.comp_median_price.toFixed(2) : '',
    });
  }

  async function startSweep(onlyStale = false) {
    const msg = onlyStale
      ? 'Refresh competitor data for all SKUs not researched in the last 7 days?'
      : 'Refresh competitor data for ALL in-stock SKUs? This takes ~45-60 minutes.';
    if (!window.confirm(msg)) return;
    try {
      await api.post('/competitor-research/run-now', { onlyStale });
      setSweepRunning(true);
    } catch (err) {
      alert('Could not start sweep: ' + (err.response?.data?.error || err.message));
    }
  }

  async function bulkRefreshSelected() {
    const skus = Object.keys(selected).filter(k => selected[k]);
    if (!skus.length) return;
    if (!window.confirm(`Refresh competitor data for ${skus.length} selected SKUs?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post('/competitor-research/refresh-bulk', { skus: skus.slice(0, 50) });
      setSelected({});
      load(page);
      alert(`Refreshed ${r.data.success?.length || 0} SKUs (${r.data.failed?.length || 0} failed)`);
    } finally { setBulkBusy(false); }
  }

  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">📊 Competitor Research</div>
        {sweepRunning && (
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, fontSize:12, color:'var(--accent)',
            background:'rgba(108,99,255,.1)', padding:'6px 12px', borderRadius:6 }}>
            <span className="dot yellow"></span> Sweep running — refreshing in background
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:12, marginBottom:20 }}>
          {[
            { label:'In stock total',     value: stats.total_in_stock,    color:'var(--text)' },
            { label:'With comp data',     value: stats.with_data || 0,    color:'var(--accent2)' },
            { label:'Overpriced (>10%)',  value: stats.overpriced || 0,   color:'var(--danger)' },
            { label:'Underpriced (>10%)', value: stats.underpriced || 0,  color:'var(--warn)' },
            { label:'Stale or missing',   value: stats.stale_or_missing || 0, color:'var(--muted)' },
          ].map(s => (
            <div key={s.label} style={{
              background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12,
              padding:'16px 14px', textAlign:'center',
            }}>
              <div style={{ fontSize:26, fontWeight:700, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="inv-toolbar" style={{ flexWrap:'wrap' }}>
        <form onSubmit={handleSearch} className="inv-search">
          <span className="inv-search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SKU or title..." />
        </form>

        <div className="inv-filter-group">
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`btn ${filter === f.id ? 'btn-primary' : 'btn-ghost'}`}
              style={{ fontSize:12, padding:'6px 10px' }}>
              {f.label}
            </button>
          ))}
        </div>

        <select value={sort} onChange={e => setSort(e.target.value)} className="inv-sort-select">
          {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>

        <span className="inv-results-count">{total} items</span>

        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          {selectedCount > 0 && (
            <button className="btn btn-primary" disabled={bulkBusy} onClick={bulkRefreshSelected}>
              {bulkBusy ? '⏳' : '🔁'} Refresh selected ({selectedCount})
            </button>
          )}
          <button className="btn btn-ghost" disabled={sweepRunning} onClick={() => startSweep(true)}>
            Sweep stale only
          </button>
          <button className="btn btn-primary" disabled={sweepRunning} onClick={() => startSweep(false)}>
            {sweepRunning ? '⏳ Running...' : 'Refresh all'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding:0, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:'1px solid var(--border)', background:'rgba(255,255,255,.02)' }}>
              <th style={{ padding:'10px 12px', textAlign:'left', width:32 }}>
                <input type="checkbox" checked={selectedCount === rows.length && rows.length > 0}
                  onChange={e => {
                    const n = {};
                    if (e.target.checked) rows.forEach(r => { n[r.sku] = true; });
                    setSelected(n);
                  }}/>
              </th>
              <th style={{ padding:'10px 12px', textAlign:'left' }}>Item</th>
              <th style={{ padding:'10px 12px', textAlign:'right' }}>My price</th>
              <th style={{ padding:'10px 12px', textAlign:'right' }}>Qty</th>
              <th style={{ padding:'10px 12px', textAlign:'right' }}>Comp median</th>
              <th style={{ padding:'10px 12px', textAlign:'right' }}>Δ%</th>
              <th style={{ padding:'10px 12px', textAlign:'center' }}>Listings</th>
              <th style={{ padding:'10px 12px', textAlign:'left' }}>Last research</th>
              <th style={{ padding:'10px 12px', textAlign:'right', width:200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>
                No items match this filter. Try "All" or trigger a sweep to populate data.
              </td></tr>
            )}
            {rows.map(r => {
              let img = null;
              try { img = JSON.parse(r.images || '[]')[0] || null; } catch {}
              const d = r.delta_pct;
              const _cl = r.comp_listings || [];
              const _compPacks = _cl.filter(l => !l.mine).map(l => parsePack(l.title));
              const _ourPack = parsePack(r.title);
              const _mixedPacks = (new Set(_compPacks).size > 1) || (_compPacks.length > 0 && !_compPacks.every(p => p === _ourPack));
              const dStr = d == null ? '—' : (d > 0 ? '+' : '') + d.toFixed(1) + '%';
              return (
                <tr key={r.sku} style={{ borderBottom:'1px solid var(--border)' }}>
                  <td style={{ padding:'10px 12px' }}>
                    <input type="checkbox" checked={!!selected[r.sku]}
                      onChange={e => setSelected(prev => ({ ...prev, [r.sku]: e.target.checked }))}/>
                  </td>
                  <td style={{ padding:'10px 12px' }}>
                    <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                      {img
                        ? <img src={img} alt="" style={{ width:36, height:36, objectFit:'cover', borderRadius:4, border:'1px solid var(--border)' }} />
                        : <div style={{ width:36, height:36, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:4 }} />
                      }
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--accent2)' }}>{r.sku}</div>
                        <div style={{ fontSize:12, color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:380 }}>
                          {r.title || '—'}
                        </div>
                        {r.location && <div style={{ fontSize:11, fontFamily:'monospace', color:'var(--muted)' }}>{r.location}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:600 }}>{fmtPrice(r.my_price)}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right', color: (r.quantity||0)>0 ? 'var(--text)' : 'var(--muted)' }}>{r.quantity ?? '—'}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right' }}>
                    {fmtPrice(r.comp_median_price)}
                    {_mixedPacks && <span title="Competitor set mixes pack sizes vs your item — open Sources (🔍) to compare delivered & per-unit price" style={{ marginLeft:5, cursor:'help' }}>⚠️</span>}
                  </td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, color: deltaColor(d) }}>{dStr}</td>
                  <td style={{ padding:'10px 12px', textAlign:'center', fontSize:12 }}>
                    {r.comp_listings && r.comp_listings.length > 0 ? (
                      <button className="btn btn-ghost" title="View the competitor listings used for pricing"
                        onClick={() => setSources({ sku:r.sku, title:r.title, listings:r.comp_listings, researched_at:r.researched_at })}
                        style={{ fontSize:11, padding:'3px 9px' }}>
                        {(r.comp_listings.filter(l=>!l.mine).length)} 🔍
                      </button>
                    ) : <span style={{ color:'var(--muted)' }}>{r.comp_listings_checked || 0}</span>}
                  </td>
                  <td style={{ padding:'10px 12px', fontSize:11, color:'var(--muted)' }}>
                    {fmtAge(r.researched_at)}
                    {r.status === 'no_results' && <span style={{ color:'var(--warn)' }}> · no results</span>}
                    {r.status === 'error' && <span style={{ color:'var(--danger)' }} title={r.error_message}> · error</span>}
                  </td>
                  <td style={{ padding:'10px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                    <button className="btn btn-ghost" disabled={!!busy[r.sku]} onClick={() => refreshOne(r.sku)}
                      style={{ fontSize:11, padding:'4px 8px', marginRight:4 }}>
                      {busy[r.sku] === 'refresh' ? '⏳' : '🔁'}
                    </button>
                    <button className="btn btn-primary" disabled={!!busy[r.sku] || r.comp_median_price == null}
                      onClick={() => openEditor(r)}
                      style={{ fontSize:11, padding:'4px 8px' }}>
                      Set price…
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Price editor modal */}
      {editor && <PriceEditor editor={editor} onChange={setEditor} onClose={() => setEditor(null)} onApply={applyPrice} busy={!!busy[editor.sku]} />}

      {/* Sources modal */}
      {sources && <SourcesModal data={sources} onClose={() => setSources(null)} />}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="pagination">
          <button className="btn btn-ghost" disabled={page === 1}
            onClick={() => { const p = page - 1; setPage(p); load(p); }}>Prev</button>
          <span className="page-info">Page {page} of {Math.ceil(total / LIMIT)}</span>
          <button className="btn btn-ghost" disabled={page >= Math.ceil(total / LIMIT)}
            onClick={() => { const p = page + 1; setPage(p); load(p); }}>Next</button>
        </div>
      )}
    </div>
  );
}
